import * as React from 'react';
import {
    Alert, Box, Card, CardContent, Chip, Grid, LinearProgress, Link as MuiLink, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink } from 'react-router-dom';
import { DashboardAiHealthDto } from '../../models/dtos/AiDto';
import {
    AiBudgetStateChip, AiHealthChip, budgetStateColor, formatAiDate, formatAiRate, formatAiUsd,
} from './AiShared';

function PanelCard({ title, count, children, to }: { title: string; count?: number; children: React.ReactNode; to?: string }) {
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
                    <Stack direction="row" gap={1} alignItems="center">
                        {count != null && <Chip size="small" variant="outlined" label={count} />}
                        {to && (
                            <MuiLink component={RouterLink} to={to} variant="caption" underline="hover">Manage</MuiLink>
                        )}
                    </Stack>
                </Stack>
                {children}
            </CardContent>
        </Card>
    );
}

function Empty({ text }: { text: string }) {
    return <Typography variant="body2" color="text.secondary">{text}</Typography>;
}

/** Budget progress: bar coloured by state, capped at 100 %. */
export function BudgetProgress({ percent, state }: { percent: number; state?: string | null }) {
    const color = budgetStateColor(state);
    return (
        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 120 }}>
            <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, percent))}
                color={color === 'default' || color === 'info' || color === 'secondary' ? 'primary' : color}
                sx={{ flex: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" sx={{ minWidth: 44, textAlign: 'right' }}>{percent.toFixed(1)}%</Typography>
        </Stack>
    );
}

/** AI health tiles: overall, budgets, provider keys, MCP servers, tasks/agents (+ jobs). */
export default function AiHealthPanels({ health, loadError }: { health: DashboardAiHealthDto | null; loadError?: string | null }) {
    if (health == null) {
        return <Alert severity="warning">{loadError ?? 'AI health is not available.'}</Alert>;
    }
    if (!health.statusAvailable) {
        return <Alert severity="warning">{health.errorMessage || 'AI service status is currently unavailable.'}</Alert>;
    }
    const budgets = health.budgets ?? [];
    const keys = health.providerKeys ?? [];
    const servers = health.mcpServers ?? [];
    const tasks = health.tasks ?? [];
    const agents = health.agents ?? [];
    const jobs = health.jobs ?? [];
    const unhealthyCount = [...keys, ...servers, ...tasks, ...agents].filter(x => x.status !== 'HEALTHY').length
        + budgets.filter(b => b.state !== 'OK').length;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={4}>
                <PanelCard title="Overall">
                    <Stack gap={1}>
                        <Box><AiHealthChip status={health.status} size="medium" /></Box>
                        <Typography variant="body2" color="text.secondary">
                            {unhealthyCount === 0 ? 'Everything is healthy.' : `${unhealthyCount} item(s) need attention.`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Generated {formatAiDate(health.generatedAt)}</Typography>
                        {jobs.length > 0 && (
                            <Table size="small">
                                <TableHead>
                                    <TableRow><TableCell>Job</TableCell><TableCell>Last success</TableCell></TableRow>
                                </TableHead>
                                <TableBody>
                                    {jobs.map(j => (
                                        <TableRow key={j.name}>
                                            <TableCell>
                                                <Typography variant="body2">{j.name}</Typography>
                                                {j.lastError && <Typography variant="caption" color="error">{j.lastError}</Typography>}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption">{formatAiDate(j.lastSuccessAt)}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Stack>
                </PanelCard>
            </Grid>
            <Grid item xs={12} md={6} lg={8}>
                <PanelCard title="Budgets" count={budgets.length} to="/ai/budgets">
                    {budgets.length === 0 ? <Empty text="No budgets." /> : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Budget</TableCell>
                                    <TableCell>Spent / limit</TableCell>
                                    <TableCell sx={{ minWidth: 160 }}>Used</TableCell>
                                    <TableCell>Forecast</TableCell>
                                    <TableCell>Blocked</TableCell>
                                    <TableCell>State</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {budgets.map(b => {
                                    const scope = b.scope?.taskCode ? `task ${b.scope.taskCode}`
                                        : b.scope?.agentCode ? `agent ${b.scope.agentCode}` : 'all';
                                    return (
                                        <TableRow key={b.budgetId}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>{b.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {b.period} · {b.action} · {scope}{b.scope?.caller ? ` · ${b.scope.caller}` : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{formatAiUsd(b.spentUsd)} / {formatAiUsd(b.limitUsd)}</TableCell>
                                            <TableCell><BudgetProgress percent={b.percent} state={b.state} /></TableCell>
                                            <TableCell>{formatAiUsd(b.forecastUsd)}</TableCell>
                                            <TableCell>{b.blockedInPeriod}</TableCell>
                                            <TableCell>
                                                <AiBudgetStateChip state={b.state} />
                                                {b.since && <Typography variant="caption" display="block" color="text.secondary">since {formatAiDate(b.since)}</Typography>}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </PanelCard>
            </Grid>
            <Grid item xs={12} md={6}>
                <PanelCard title="Provider keys" count={keys.length} to="/ai/models">
                    {keys.length === 0 ? <Empty text="No provider keys." /> : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Key</TableCell>
                                    <TableCell>Calls 15m</TableCell>
                                    <TableCell>Error rate</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {keys.map(k => (
                                    <TableRow key={k.id}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>{k.provider.toLowerCase()} / {k.name}</Typography>
                                            <Stack direction="row" gap={0.5} sx={{ mt: 0.5 }}>
                                                {!k.configured && <Chip size="small" color="error" label="not configured" />}
                                                {k.keyStatus === 'DISABLED' && <Chip size="small" variant="outlined" label="DISABLED" />}
                                            </Stack>
                                            {k.lastError && (
                                                <Tooltip title={k.lastError}>
                                                    <Typography variant="caption" color="error" display="block" noWrap sx={{ maxWidth: 260 }}>
                                                        {formatAiDate(k.lastErrorAt)}: {k.lastError}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>{k.calls15m} ({k.errors15m} err)</TableCell>
                                        <TableCell>{formatAiRate(k.errorRate15m)}</TableCell>
                                        <TableCell><AiHealthChip status={k.status} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </PanelCard>
            </Grid>
            <Grid item xs={12} md={6}>
                <PanelCard title="MCP servers" count={servers.length} to="/ai/mcp-servers">
                    {servers.length === 0 ? <Empty text="No MCP servers." /> : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Server</TableCell>
                                    <TableCell>Last OK</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {servers.map(s => (
                                    <TableRow key={s.code}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>{s.name || s.code}</Typography>
                                            <Typography variant="caption" color="text.secondary">{s.code}</Typography>
                                            {s.lastError && (s.consecutiveFailures ?? 1) > 0 && (
                                                <Tooltip title={s.lastError}>
                                                    <Typography variant="caption" color="error" display="block" noWrap sx={{ maxWidth: 260 }}>
                                                        {formatAiDate(s.lastErrorAt)}: {s.lastError}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell><Typography variant="caption">{formatAiDate(s.lastOkAt)}</Typography></TableCell>
                                        <TableCell>
                                            <AiHealthChip status={s.status} />
                                            {(s.consecutiveFailures ?? 0) > 0 && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {s.consecutiveFailures} failed since {formatAiDate(s.failingSince)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </PanelCard>
            </Grid>
            <Grid item xs={12} md={6}>
                <PanelCard title="Tasks (last hour)" count={tasks.length} to="/ai/tasks">
                    {tasks.length === 0 ? <Empty text="No task activity in the last hour and nothing unhealthy." /> : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Task</TableCell>
                                    <TableCell>Calls</TableCell>
                                    <TableCell>Errors</TableCell>
                                    <TableCell>Fallback</TableCell>
                                    <TableCell>Blocked</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tasks.map(t => (
                                    <TableRow key={t.code}>
                                        <TableCell>
                                            <MuiLink component={RouterLink} to={`/ai/tasks/${encodeURIComponent(t.code)}`} underline="hover" variant="body2">{t.code}</MuiLink>
                                        </TableCell>
                                        <TableCell>{t.calls1h}</TableCell>
                                        <TableCell>{t.errors1h} ({formatAiRate(t.errorRate1h)})</TableCell>
                                        <TableCell>{formatAiRate(t.fallbackRate1h)}</TableCell>
                                        <TableCell>{t.blocked1h}</TableCell>
                                        <TableCell><AiHealthChip status={t.status} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </PanelCard>
            </Grid>
            <Grid item xs={12} md={6}>
                <PanelCard title="Agents (last hour)" count={agents.length} to="/ai/agents">
                    {agents.length === 0 ? <Empty text="No agent runs in the last hour and nothing unhealthy." /> : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Agent</TableCell>
                                    <TableCell>Runs</TableCell>
                                    <TableCell>Failed</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {agents.map(a => (
                                    <TableRow key={a.code}>
                                        <TableCell>
                                            <MuiLink component={RouterLink} to={`/ai/agents/${encodeURIComponent(a.code)}`} underline="hover" variant="body2">{a.code}</MuiLink>
                                        </TableCell>
                                        <TableCell>{a.runs1h}</TableCell>
                                        <TableCell>{a.failedRuns1h}</TableCell>
                                        <TableCell><AiHealthChip status={a.status} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </PanelCard>
            </Grid>
        </Grid>
    );
}

/** Compact tile for the Health page overview row. */
export function AiHealthOverviewTile({ health, loadError }: { health: DashboardAiHealthDto | null; loadError?: string | null }) {
    const available = health != null && health.statusAvailable;
    const keys = health?.providerKeys ?? [];
    const servers = health?.mcpServers ?? [];
    const budgets = health?.budgets ?? [];
    const tasks = health?.tasks ?? [];
    const agents = health?.agents ?? [];
    const bad = <T extends { status: string }>(items: T[]) => items.filter(i => i.status !== 'HEALTHY').length;
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PsychologyIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight="bold">AI</Typography>
                    </Box>
                    {available && <AiHealthChip status={health?.status} />}
                </Box>
                {health == null ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>{loadError ?? 'AI health unavailable'}</Alert>
                ) : !health.statusAvailable ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>{health.errorMessage || 'Status unavailable'}</Alert>
                ) : (
                    <Stack gap={0.5} sx={{ mb: 1 }}>
                        {([
                            ['Provider keys', keys.length, bad(keys)],
                            ['MCP servers', servers.length, bad(servers)],
                            ['Budgets', budgets.length, budgets.filter(b => b.state !== 'OK').length],
                            ['Tasks (1h)', tasks.length, bad(tasks)],
                            ['Agents (1h)', agents.length, bad(agents)],
                        ] as Array<[string, number, number]>).map(([label, total, unhealthy]) => (
                            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">{label}</Typography>
                                <Typography variant="body2" fontWeight="bold" color={unhealthy > 0 ? 'error.main' : undefined}>
                                    {total}{unhealthy > 0 ? ` (${unhealthy} not OK)` : ''}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                )}
                <Box
                    component="a"
                    href="#section-ai"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                >
                    <Typography variant="caption" fontWeight="bold">View details</Typography>
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Box>
            </CardContent>
        </Card>
    );
}
